import { useSnackbar } from '../../../global/config/useSnackbar';
import { useEndpointFetch } from '../../../global/network/useEndpointFetch';
import { useVolunteerCommonProfileContext } from './VolunteerCommonProfileContext';

const useSaveCommentsFetch = () => {
  return useEndpointFetch<{ volunteerId: string; comments: string }>('/vm/volunteer/manage/comments');
};

export const useSaveRolesFetch = () => {
  return useEndpointFetch<{ volunteerId: string; activityRoleIds: string[] }>(
    '/vm/volunteer/manage/update-activity-roles'
  );
};

export const useSaveVolunteerTagsFetch = () => {
  return useEndpointFetch<{ volunteerId: string; volunteerTagIds: string[] }>('/vm/volunteer/manage/update-tags');
};
export const useFlagVolunteerFetch = () => {
  return useEndpointFetch<{ volunteerId: string; reason: string }>('/vm/volunteer/manage/flag');
};
export const useRetireVolunteerFetch = () => {
  return useEndpointFetch<{ volunteerId: string }>('/vm/volunteer/manage/retire');
};
export const useReactivateVolunteerFetch = () => {
  return useEndpointFetch<{ volunteerId: string }>('/vm/volunteer/manage/unretire');
};
type ReturnVolunteerToApplicantResponse = {
  returned: Array<{ volunteerId: string; name: string }>;
  errors: Array<{ volunteerId: string; error: string }>;
};

export const useReturnVolunteerToApplicantFetch = () => {
  return useEndpointFetch<{ volunteerIds: string[] }, ReturnVolunteerToApplicantResponse>(
    '/vm/volunteer/manage/return-to-applicant'
  );
};

export const useVolunteerProfileActions = () => {
  const saveComments = useSaveCommentsFetch();
  const saveRoles = useSaveRolesFetch();
  const saveVolunteerTags = useSaveVolunteerTagsFetch();
  const flagVolunteer = useFlagVolunteerFetch();
  const retireVolunteer = useRetireVolunteerFetch();
  const reactivateVolunteer = useReactivateVolunteerFetch();
  const returnVolunteerToApplicant = useReturnVolunteerToApplicantFetch();
  const { updateParentVolunteerActions } = useVolunteerCommonProfileContext();
  const { setSnackbar } = useSnackbar();
  const runSaveComments = ({
    volunteerId,
    comments,
    handleSuccess,
  }: {
    volunteerId: string;
    comments: string;
    handleSuccess?: () => void;
  }) => {
    saveComments
      .run({
        volunteerId,
        comments,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to save comment',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Comment saved',
          variant: 'success',
        });
        if (updateParentVolunteerActions) {
          const { updateVolunteerWithSavedComment } = updateParentVolunteerActions;
          if (updateVolunteerWithSavedComment) updateVolunteerWithSavedComment(comments);
        }
        if (handleSuccess) handleSuccess();
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Unable to save comment',
          variant: 'error',
        })
      );
  };

  const runSaveRoles = ({
    volunteerId,
    activityRoleIds,
    handleSuccess,
  }: {
    volunteerId: string;
    activityRoleIds: string[];
    handleSuccess?: (roleIds: string[]) => void;
  }) => {
    saveRoles
      .run({
        volunteerId,
        activityRoleIds,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to save role',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Roles saved',
          variant: 'success',
        });
        if (handleSuccess) handleSuccess(activityRoleIds);
      });
  };
  const runSaveVolunteerTags = ({
    volunteerId,
    volunteerTagIds,
    handleSuccess,
  }: {
    volunteerId: string;
    volunteerTagIds: string[];
    handleSuccess?: (tagIds: string[]) => void;
  }) => {
    saveVolunteerTags
      .run({
        volunteerId,
        volunteerTagIds,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to save tags',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Tags saved',
          variant: 'success',
        });
        if (handleSuccess) handleSuccess(volunteerTagIds);
      });
  };

  const runFlagVolunteer = ({
    volunteerId,
    reason,
    handleSuccess,
  }: {
    volunteerId: string;
    reason: string;
    handleSuccess?: () => void;
  }) => {
    flagVolunteer
      .run({
        volunteerId,
        reason,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to flag volunteer',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Volunteer flagged',
          variant: 'success',
        });
        if (handleSuccess) handleSuccess();
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Unable to flag volunteer',
          variant: 'error',
        })
      );
  };
  const runRetireVolunteer = ({ volunteerId, handleSuccess }: { volunteerId: string; handleSuccess?: () => void }) => {
    retireVolunteer
      .run({
        volunteerId,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to retire volunteer',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Volunteer retired',
          variant: 'success',
        });
        if (handleSuccess) handleSuccess();
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Unable to retire volunteer',
          variant: 'error',
        })
      );
  };
  const runReactivateVolunteer = ({
    volunteerId,
    handleSuccess,
  }: {
    volunteerId: string;
    handleSuccess?: () => void;
  }) => {
    reactivateVolunteer
      .run({
        volunteerId,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to reactivate volunteer',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Volunteer reactivated',
          variant: 'success',
        });
        if (handleSuccess) handleSuccess();
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Unable to reactivate volunteer',
          variant: 'error',
        })
      );
  };

  const runReturnVolunteerToApplicant = ({
    volunteerId,
    handleSuccess,
  }: {
    volunteerId: string;
    handleSuccess?: () => void;
  }) => {
    returnVolunteerToApplicant
      .run({
        volunteerIds: [volunteerId],
      })
      .then((res) => {
        const resultErrors = res.data?.data?.errors ?? [];
        if (!res.ok || resultErrors.length > 0) {
          setSnackbar({
            open: true,
            message: resultErrors[0]?.error
              ? `Unable to return volunteer to applicant: ${resultErrors[0].error}`
              : 'Unable to return volunteer to applicant',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Volunteer returned to applicant',
          variant: 'success',
        });
        if (handleSuccess) handleSuccess();
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Unable to return volunteer to applicant',
          variant: 'error',
        })
      );
  };

  return {
    runSaveComments,
    runSaveCommentsLoading: saveComments.isLoading,
    runSaveCommentsError: saveComments.error,
    runSaveRoles,
    runSaveRolesLoading: saveRoles.isLoading,
    runSaveRolesError: saveRoles.error,
    runSaveVolunteerTags,
    runSaveVolunteerTagsLoading: saveVolunteerTags.isLoading,
    runSaveVolunteerTagsError: saveVolunteerTags.error,
    runFlagVolunteer,
    runFlagVolunteerLoading: flagVolunteer.isLoading,
    runFlagVolunteerError: flagVolunteer.error,
    runRetireVolunteer,
    runRetireVolunteerLoading: retireVolunteer.isLoading,
    runReactivateVolunteer,
    runReactivateVolunteerLoading: reactivateVolunteer.isLoading,
    runReturnVolunteerToApplicant,
    runReturnVolunteerToApplicantLoading: returnVolunteerToApplicant.isLoading,
  };
};

export type VolunteerProfileActions = {
  updateVolunteerAsRetired?: () => void;
  updateVolunteerAsExited?: () => void;
  updateVolunteerAsReactivated?: () => void;
  updateVolunteerWithSavedComment?: (comments: string) => void;
  updateVolunteerWithSavedActivityRoles?: (activityRoleIds: string[]) => void;
  updateVolunteerWithSavedTags?: (volunteerTagIds: string[]) => void;
};

